import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { GitStatus, GitActionResult } from '../../types/project.js';
import * as projectApi from '../../services/project-api.js';

@customElement('cp-git-panel')
export class CpGitPanel extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) projectId = 0;

  @state() private _status: GitStatus | null = null;
  @state() private _loading = false;
  @state() private _actionLoading = '';
  @state() private _error = '';
  @state() private _feedback: GitActionResult | null = null;
  @state() private _commitMessage = '';
  @state() private _showCommitForm = false;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('projectId') && this.projectId > 0) {
      void this._loadStatus();
    }
  }

  private async _loadStatus(): Promise<void> {
    if (!this.projectId) return;
    this._loading = true;
    this._error = '';

    try {
      this._status = await projectApi.getGitStatus(this.projectId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Git-Status konnte nicht geladen werden';
    } finally {
      this._loading = false;
    }
  }

  private async _handlePull(): Promise<void> {
    this._actionLoading = 'pull';
    this._feedback = null;

    try {
      this._feedback = await projectApi.gitPull(this.projectId);
      await this._loadStatus();
    } catch (e) {
      this._feedback = {
        success: false,
        message: 'Pull fehlgeschlagen',
        details: e instanceof Error ? e.message : undefined,
      };
    } finally {
      this._actionLoading = '';
    }
  }

  private async _handlePush(): Promise<void> {
    this._actionLoading = 'push';
    this._feedback = null;

    try {
      this._feedback = await projectApi.gitPush(this.projectId);
      await this._loadStatus();
    } catch (e) {
      this._feedback = {
        success: false,
        message: 'Push fehlgeschlagen',
        details: e instanceof Error ? e.message : undefined,
      };
    } finally {
      this._actionLoading = '';
    }
  }

  private async _handleCommit(): Promise<void> {
    if (!this._commitMessage.trim()) return;
    this._actionLoading = 'commit';
    this._feedback = null;

    try {
      this._feedback = await projectApi.gitCommit(this.projectId, { message: this._commitMessage.trim() });
      this._commitMessage = '';
      this._showCommitForm = false;
      await this._loadStatus();
    } catch (e) {
      this._feedback = {
        success: false,
        message: 'Commit fehlgeschlagen',
        details: e instanceof Error ? e.message : undefined,
      };
    } finally {
      this._actionLoading = '';
    }
  }

  private _onCommitInput(e: Event): void {
    this._commitMessage = (e.target as HTMLInputElement).value;
  }

  private _toggleCommitForm(): void {
    this._showCommitForm = !this._showCommitForm;
    this._commitMessage = '';
  }

  private _dismissFeedback(): void {
    this._feedback = null;
  }

  private _renderSkeleton() {
    return html`
      <div class="cp-git-panel">
        <div class="cp-git-panel__skeleton">
          <div class="cp-git-panel__skeleton-line cp-git-panel__skeleton-line--branch"></div>
          <div class="cp-git-panel__skeleton-line cp-git-panel__skeleton-line--status"></div>
        </div>
      </div>
    `;
  }

  private _renderFeedback() {
    if (!this._feedback) return nothing;
    const cls = this._feedback.success ? 'cp-git-panel__feedback--success' : 'cp-git-panel__feedback--error';
    return html`
      <div class="cp-git-panel__feedback ${cls}" @click=${this._dismissFeedback}>
        <span class="cp-git-panel__feedback-msg">${this._feedback.message}</span>
        ${this._feedback.details ? html`
          <span class="cp-git-panel__feedback-details">${this._feedback.details}</span>
        ` : nothing}
      </div>
    `;
  }

  private _renderCommitForm() {
    if (!this._showCommitForm) return nothing;
    const isCommitting = this._actionLoading === 'commit';
    return html`
      <div class="cp-git-panel__commit-form">
        <input
          class="cp-git-panel__commit-input"
          type="text"
          placeholder="Commit-Message eingeben..."
          .value=${this._commitMessage}
          @input=${this._onCommitInput}
          @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this._handleCommit(); }}
          ?disabled=${isCommitting}
        />
        <div class="cp-git-panel__commit-actions">
          <button
            class="cp-git-panel__btn cp-git-panel__btn--primary"
            ?disabled=${!this._commitMessage.trim() || isCommitting}
            @click=${this._handleCommit}
          >
            ${isCommitting ? 'Committing...' : 'Commit'}
          </button>
          <button
            class="cp-git-panel__btn cp-git-panel__btn--secondary"
            @click=${this._toggleCommitForm}
            ?disabled=${isCommitting}
          >
            Abbrechen
          </button>
        </div>
      </div>
    `;
  }

  render() {
    if (this._loading) return this._renderSkeleton();

    if (this._error) {
      return html`<div class="cp-git-panel"><p class="cp-git-panel__error">${this._error}</p></div>`;
    }

    if (!this._status) return nothing;

    const s = this._status;
    const hasChanges = !s.isClean;
    const isPulling = this._actionLoading === 'pull';
    const isPushing = this._actionLoading === 'push';

    return html`
      <div class="cp-git-panel">
        <div class="cp-git-panel__header">
          <div class="cp-git-panel__branch">
            <span class="cp-git-panel__branch-icon">&#9699;</span>
            <span class="cp-git-panel__branch-name">${s.branch}</span>
          </div>
          <button class="cp-git-panel__refresh" @click=${this._loadStatus} title="Status aktualisieren">&#8635;</button>
        </div>

        <div class="cp-git-panel__stats">
          ${hasChanges ? html`
            <span class="cp-git-panel__stat cp-git-panel__stat--modified">${s.modified} geaendert</span>
          ` : html`
            <span class="cp-git-panel__stat cp-git-panel__stat--clean">Clean</span>
          `}
          ${s.staged > 0 ? html`
            <span class="cp-git-panel__stat cp-git-panel__stat--staged">${s.staged} staged</span>
          ` : nothing}
          ${s.ahead > 0 ? html`
            <span class="cp-git-panel__stat cp-git-panel__stat--ahead">${s.ahead} ahead</span>
          ` : nothing}
          ${s.behind > 0 ? html`
            <span class="cp-git-panel__stat cp-git-panel__stat--behind">${s.behind} behind</span>
          ` : nothing}
        </div>

        ${hasChanges && s.files.length > 0 ? html`
          <details class="cp-git-panel__files-details">
            <summary class="cp-git-panel__files-summary">${s.files.length} Dateien</summary>
            <ul class="cp-git-panel__files">
              ${s.files.map(f => html`
                <li class="cp-git-panel__file">
                  <span class="cp-git-panel__file-status">${f.status}</span>
                  <span class="cp-git-panel__file-path">${f.path}</span>
                </li>
              `)}
            </ul>
          </details>
        ` : nothing}

        <div class="cp-git-panel__actions">
          <button
            class="cp-git-panel__btn cp-git-panel__btn--action"
            ?disabled=${isPulling || isPushing}
            @click=${this._handlePull}
          >
            ${isPulling ? 'Pulling...' : 'Pull'}
          </button>
          <button
            class="cp-git-panel__btn cp-git-panel__btn--action"
            ?disabled=${isPulling || isPushing}
            @click=${this._handlePush}
          >
            ${isPushing ? 'Pushing...' : 'Push'}
          </button>
          <button
            class="cp-git-panel__btn cp-git-panel__btn--action"
            ?disabled=${this._actionLoading !== ''}
            @click=${this._toggleCommitForm}
          >
            Commit
          </button>
        </div>

        ${this._renderCommitForm()}
        ${this._renderFeedback()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-git-panel': CpGitPanel;
  }
}
